import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages, categories } from '../data/mock';
import { Link } from 'react-router-dom';
import { Eye, BookOpen, ArrowRight, AlertCircle, CheckCircle, XCircle, Sparkles } from 'lucide-react';

const HowToUsePage = () => {
  useEffect(() => {
    document.title = 'How to Use Your Eyewear | Clarity Lens Co';
  }, []);

  const guides = [
    {
      title: 'Blue Light Blocking Glasses',
      description: 'Protect your eyes during screen time',
      tips: [
        'Wear during extended computer or phone use',
        'Ideal for 2+ hours of screen time daily',
        'Use in the evening to improve sleep quality',
        'Perfect for gaming sessions'
      ],
      avoid: [
        'Wearing outdoors as primary sun protection',
        'Using as a substitute for regular eye exams'
      ]
    },
    {
      title: 'Polarized Sunglasses',
      description: 'Maximum protection from sun and glare',
      tips: [
        'Essential for driving to reduce glare',
        'Perfect for beach, water, and snow activities',
        'Wear during outdoor sports for clarity',
        'Use for all-day outdoor wear'
      ],
      avoid: [
        'Looking at LCD screens (may appear dark)',
        'Night driving (reduces visibility)'
      ]
    },
    {
      title: 'Reading Glasses',
      description: 'Clear vision for close-up work',
      tips: [
        'Use for reading books, newspapers, menus',
        'Ideal for crafting and detailed work',
        'Position 14-16 inches from your eyes',
        'Take breaks every 20 minutes'
      ],
      avoid: [
        'Wearing for distance viewing',
        'Using while walking or driving'
      ]
    },
    {
      title: 'Night Driving Glasses',
      description: 'Safer driving in low-light conditions',
      tips: [
        'Wear specifically for night driving',
        'Helps reduce headlight glare',
        'Improves contrast in low light',
        'Also useful for foggy/rainy conditions'
      ],
      avoid: [
        'Wearing during daytime driving',
        'Using as regular sunglasses'
      ]
    }
  ];

  const careSteps = [
    {
      step: '1',
      title: 'Clean Regularly',
      desc: 'Use the included microfiber cloth and lens cleaner. Rinse lenses with water before wiping to remove debris.'
    },
    {
      step: '2',
      title: 'Store Properly',
      desc: 'Always store in the provided case when not in use. Keep away from extreme heat and direct sunlight.'
    },
    {
      step: '3',
      title: 'Handle with Care',
      desc: 'Use both hands when putting on or removing glasses. Avoid placing lenses face-down on surfaces.'
    },
    {
      step: '4',
      title: 'Adjust Fit',
      desc: 'If frames feel loose, visit an optician for adjustment. Never bend frames yourself.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Hero Banner */}
        <section className="relative h-[200px] overflow-hidden bg-neutral-100">
          <img 
            src={siteImages.heroLifestyle}
            alt="How to Use"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-lg">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white text-xs font-medium mb-4">
                  <BookOpen className="w-3.5 h-3.5" />
                  User Guide
                </span>
                <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold tracking-tight">
                  How to Use Your Eyewear
                </h1>
              </div>
            </div>
          </div>
        </section>

        {/* Product Guides */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl text-neutral-900 font-semibold mb-2">
                Product Usage Guides
              </h2>
              <p className="text-neutral-600">Get the most out of your eyewear with these tips</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {guides.map((guide, i) => (
                <div key={i} className="bg-neutral-50 rounded-2xl p-6">
                  <h3 className="font-display text-lg text-neutral-900 font-semibold mb-1">
                    {guide.title}
                  </h3>
                  <p className="text-neutral-500 text-sm mb-5">{guide.description}</p>
                  
                  <div className="mb-4">
                    <p className="text-sm font-medium text-green-700 flex items-center gap-2 mb-3">
                      <CheckCircle className="w-4 h-4" />
                      Best For
                    </p>
                    <ul className="space-y-2">
                      {guide.tips.map((tip, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-neutral-600">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-red-700 flex items-center gap-2 mb-3">
                      <XCircle className="w-4 h-4" />
                      Avoid
                    </p>
                    <ul className="space-y-2">
                      {guide.avoid.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-neutral-600">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Care Instructions */}
        <section className="py-16 bg-neutral-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full text-white text-xs font-medium mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                Care Guide
              </span>
              <h2 className="font-display text-2xl text-white font-semibold mb-2">
                Keep Your Eyewear Like New
              </h2>
              <p className="text-neutral-400">Follow these steps for long-lasting eyewear</p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {careSteps.map((item, i) => (
                <div key={i} className="bg-neutral-800/50 border border-neutral-700 rounded-xl p-6">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mb-4">
                    <span className="font-semibold text-neutral-900">{item.step}</span>
                  </div>
                  <h4 className="font-medium text-white mb-2">{item.title}</h4>
                  <p className="text-neutral-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pro Tips */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-medium text-neutral-900 mb-2">Pro Tips</h3>
                  <ul className="space-y-2 text-neutral-700">
                    <li>• Never clean lenses with clothing or paper products - they can scratch</li>
                    <li>• Schedule regular eye exams even when using non-prescription eyewear</li>
                    <li>• Replace your eyewear every 1-2 years for optimal protection</li>
                    <li>• Keep a backup pair for emergencies</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl text-neutral-900 font-semibold mb-4">
              Find Your Perfect Pair
            </h2>
            <p className="text-neutral-600 mb-8">
              Browse our collection and find eyewear suited for your needs
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-neutral-900 hover:bg-neutral-800 text-white font-medium rounded-full transition-colors"
            >
              Shop Now
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default HowToUsePage;
